import { Injectable, inject } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';

import { CHANGE_PASSWORD_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { selectCurrentPersonsEmail } from '../../current-person/current-person.selectors';
import { NotificationActions } from '../../notifications/notification.actions';
import { LogOutActions } from '../logout/logout.actions';
import { AmplifyService } from '../services/amplify.service';
import { UserAccountActions } from '../user-account/user-account.actions';

import { ForgotPasswordActions } from './forgot-password.actions';

@Injectable()
export class ChangePasswordEffects {
  private readonly action$ = inject(Actions);
  private readonly amplifyService = inject(AmplifyService);
  private readonly store = inject(Store);
  private readonly openChangePassword = inject(CHANGE_PASSWORD_DIALOG);

  openChangePassword$ = createEffect(() =>
    this.action$.pipe(
      ofType(UserAccountActions.openChangePasswordDialog),
      concatLatestFrom(() => this.store.select(selectCurrentPersonsEmail)),
      exhaustMap(([_, email]) =>
        this.openChangePassword()
          .afterClosed()
          .pipe(
            map(({ oldPassword, newPassword, forgotPassword, cancelled }) => {
              if (forgotPassword) return ForgotPasswordActions.openDialog({ workflow: 'SETTINGS', email });
              if (cancelled || !oldPassword || !newPassword) return UserAccountActions.cancelUpdatePassword();
              return UserAccountActions.submitUpdatePassword({
                oldPassword,
                newPassword,
              });
            })
          )
      )
    )
  );

  submitChangePassword = createEffect(() =>
    this.action$.pipe(
      ofType(UserAccountActions.submitUpdatePassword),
      switchMap(({ oldPassword, newPassword }) =>
        this.amplifyService.updatePassword(oldPassword, newPassword).pipe(
          map(() => UserAccountActions.updatePasswordSuccess()),
          catchError((error: unknown) => of(UserAccountActions.updatePasswordError({ error })))
        )
      )
    )
  );

  updatePasswordSuccess$_Notify = createEffect(() =>
    this.action$.pipe(
      ofType(UserAccountActions.updatePasswordSuccess),
      map(() =>
        NotificationActions.showSnackbar({
          message: 'Your password has been updated. Please login again with your new password.',
        })
      )
    )
  );

  updatePasswordSuccess$_LogOut = createEffect(
    () =>
      this.action$.pipe(
        ofType(UserAccountActions.updatePasswordSuccess),
        map(() => LogOutActions.initiateLogOut())
      ),
    { dispatch: false }
  );

  updatePasswordError_Notify = createEffect(() =>
    this.action$.pipe(
      ofType(UserAccountActions.updatePasswordError),
      map(() => NotificationActions.showError({ message: 'Failed to set new password. Please try again.', title: '' }))
    )
  );

  updatePasswordError_reshowDialog = createEffect(() =>
    this.action$.pipe(
      ofType(UserAccountActions.updatePasswordError),
      map(() => UserAccountActions.openChangePasswordDialog())
    )
  );
}
